/**
 * Builds a width-fitting label for a hierarchical category path.
 *
 * Strategy (in order):
 *   1. Show the full "A > B > C > D" path if it fits.
 *   2. For paths with 3+ segments, collapse the middle to "A > ... > D"
 *      if that fits.
 *   3. Otherwise truncate the ends (preferring to keep the leaf segment)
 *      with ellipses to fit within the measured width.
 *
 * Width is measured with the canvas 2D API, so callers must pass the same
 * CSS `font` shorthand (typically read from `getComputedStyle(textNode).font`)
 * used to render the label.
 */

const ELLIPSIS = '…';
const SEPARATOR = ' > ';
const COLLAPSED_SEPARATOR = ' > ... > ';
/** Conservative width used when running outside a browser (jsdom/SSR). */
const FALLBACK_CHAR_WIDTH_PX = 7;

let sharedCanvas;
const getMeasurementContext = () => {
  if (typeof document === 'undefined') return null;
  if (!sharedCanvas) sharedCanvas = document.createElement('canvas');
  return sharedCanvas.getContext('2d');
};

/** Default text-width measurement using a shared canvas. */
export const measureTextWidth = (text, font) => {
  if (!text) return 0;
  const ctx = getMeasurementContext();
  if (!ctx) return text.length * FALLBACK_CHAR_WIDTH_PX;
  ctx.font = font;
  return ctx.measureText(text).width;
};

/** Largest n such that `text.slice(0, n) + suffix` fits within `maxWidthPx`. */
const largestPrefixThatFits = (text, suffix, maxWidthPx, font, measure) => {
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (measure(text.slice(0, mid) + suffix, font) <= maxWidthPx) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
};

/** Truncate a single line of text with a trailing ellipsis to fit `maxWidthPx`. */
export const truncateLineToWidth = (text, maxWidthPx, font, measure = measureTextWidth) => {
  if (!text || maxWidthPx <= 0) return text || '';
  if (measure(text, font) <= maxWidthPx) return text;
  if (measure(ELLIPSIS, font) > maxWidthPx) return '';

  const fitting = largestPrefixThatFits(text, ELLIPSIS, maxWidthPx, font, measure);
  return text.slice(0, fitting) + ELLIPSIS;
};

/**
 * Build "first > ... > last", shrinking the ends with ellipses to fit.
 * Truncates the first segment first to keep the leaf legible.
 */
export const truncateFirstLastCollapse = (first, last, maxWidthPx, font, measure = measureTextWidth) => {
  const join = (f, l) => f + COLLAPSED_SEPARATOR + l;

  if (measure(join(first, last), font) <= maxWidthPx) return join(first, last);

  // Shrink the first segment from the right.
  for (let n = first.length - 1; n >= 1; n -= 1) {
    const truncatedFirst = first.slice(0, n) + ELLIPSIS;
    if (measure(join(truncatedFirst, last), font) <= maxWidthPx) {
      return join(truncatedFirst, last);
    }
  }

  // Replace the first segment with "…" and shrink the last from the left.
  for (let n = last.length - 1; n >= 1; n -= 1) {
    const truncatedLast = ELLIPSIS + last.slice(last.length - n);
    if (measure(join(ELLIPSIS, truncatedLast), font) <= maxWidthPx) {
      return join(ELLIPSIS, truncatedLast);
    }
  }

  // Both ends collapsed; fall back to character-level truncation.
  return truncateLineToWidth(join(ELLIPSIS, ELLIPSIS), maxWidthPx, font, measure);
};

/**
 * Picks the most informative label for `pathParts` that fits in `maxWidthPx`.
 *
 * @param {string[]} pathParts - Taxonomy path segments (root → leaf).
 * @param {number} maxWidthPx - Available width in pixels. Use `0` to skip
 *   measurement (e.g. before layout has been read) and return the full path.
 * @param {string} font - CSS `font` shorthand of the rendered label.
 * @param {(text: string, font: string) => number} [measure] - Override for tests.
 * @returns {string}
 */
export const pickCategoryPathLabel = (pathParts, maxWidthPx, font, measure = measureTextWidth) => {
  if (!pathParts || pathParts.length === 0) return '';

  const full = pathParts.join(SEPARATOR);
  if (maxWidthPx <= 0) return full;
  if (measure(full, font) <= maxWidthPx) return full;

  if (pathParts.length <= 2) {
    return truncateLineToWidth(full, maxWidthPx, font, measure);
  }

  const first = pathParts[0];
  const last = pathParts[pathParts.length - 1];
  const collapsed = first + COLLAPSED_SEPARATOR + last;
  if (measure(collapsed, font) <= maxWidthPx) return collapsed;

  return truncateFirstLastCollapse(first, last, maxWidthPx, font, measure);
};
